import styled from '@emotion/styled';
import {List, ListItem} from '@mui/material';
import React, {forwardRef, useCallback, useRef, useState} from 'react';

import {ChevronSmallRight, LoadingIcon} from '@ELib/assets/images';
import {AnimationWrapper} from '@ELib/components/AnimationWrapper';
import {EClickAwayListener} from '@ELib/components/EClickAwayListener';
import {EDynamicTooltip, ETooltip} from '@ELib/components/ETooltip';
import {mixinOverflow, mixinSVGIcon, mixinTruncate} from '@ELib/styles/mixins';
import {classy} from '@ELib/utilities/HelperFunctions';
import {useMountEffect} from '@ELib/utilities/hooks/effectHelpers';
import {Action, Positioning} from '@ELib/utilities/types';

import {usePopup} from './popup';

interface Callbacks {
  onContextMenu: (event: React.MouseEvent) => void;
}

interface ReturnType {
  contextMenu: React.ReactElement | null | undefined;
  contextMenuCallbacks: Callbacks;
}

export type ContextAction<T extends object> = Omit<
  Action<T>,
  'type' | 'inactive' | 'text'
> & {
  inactive?: boolean;
  text: string;
  StartAdornment?: React.ReactElement;
};

type ActionItem<T extends object> = Omit<
  ContextAction<T>,
  'subActions' | 'callback'
> & {
  callback: () => void;
  subMenuItems?: ActionItem<T>[];
};

const convertActionsToItems = <T extends object>(
  actions: ContextAction<T>[],
  item: T,
  close: () => void
): ActionItem<T>[] =>
  actions.map(({subActions, ...action}) => ({
    ...action,
    ...(subActions && subActions.length > 0
      ? {subMenuItems: convertActionsToItems(subActions, item, close)}
      : {}),
    callback: () => {
      if (action.callback) {
        action.callback(item);
        if (!action.keepsOpen && (!subActions || subActions.length === 0)) {
          close();
        }
      }
    },
  }));

const ContextMenu = forwardRef(
  <T extends object>(
    {
      actionItems,
    }: {
      actionItems: ActionItem<T>[];
    },
    ref: React.Ref<HTMLUListElement>
  ) => (
    <StyledContextMenu ref={ref}>
      {actionItems.map(({id: itemId, section, ...item}, index, origins) => (
        <ContextMenuItem
          key={itemId}
          id={itemId}
          {...item}
          newSection={index !== 0 && section !== origins[index - 1]?.section}
        />
      ))}
    </StyledContextMenu>
  )
);

const ContextMenuItem = <T extends object>({
  inactive = false,
  inactiveAsync,
  inactiveText,
  ...props
}: Omit<ActionItem<T>, 'section'> & {newSection: boolean}) => {
  const [inactiveState, setInactiveState] = useState(
    inactiveAsync ? true : inactive
  );
  const [inactiveTextState, setInactiveTextState] = useState(inactiveText);
  const [isLoading, setIsLoading] = useState(true);

  useMountEffect(() => {
    if (!inactiveAsync) {
      setIsLoading(false);
      return;
    }

    (async () => {
      const isInactive = await inactiveAsync(setInactiveTextState);
      setInactiveState(isInactive);
      setIsLoading(false);
    })();
  });

  return (
    <MenuItem
      {...props}
      inactive={inactiveState}
      inactiveText={inactiveTextState}
      isLoading={isLoading}
    />
  );
};

const MenuItem = <T extends object>({
  id,
  Icon,
  text,
  inactiveText,
  callback,
  subMenuItems,
  newSection,
  inactive,
  isLoading,
  StartAdornment,
}: Omit<ActionItem<T>, 'section' | 'inactive'> & {
  newSection: boolean;
  inactive: boolean;
  isLoading: boolean;
}) => {
  const [targetRef, setTargetRef] = useState<HTMLLIElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const subMenuRef = useRef<HTMLUListElement>(null);

  const {popup: contextSubMenuPopup} = usePopup({
    disableBackdrop: true,
    content: subMenuItems ? (
      <ContextMenu ref={subMenuRef} actionItems={subMenuItems}></ContextMenu>
    ) : (
      <></>
    ),
    isVisible: true,
    disablePortal: true,
    placement: Positioning.TopRightOutsetDown,
    target: targetRef,
  });

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (!inactive && (!subMenuItems || subMenuItems.length === 0)) {
        callback();
      }
    },
    [callback, subMenuItems, inactive]
  );

  const openSubMenu = useCallback(() => {
    if (subMenuItems && subMenuItems.length > 0 && !inactive) {
      setExpanded(true);
    }
  }, [inactive, subMenuItems]);

  const closeSubMenu = useCallback(
    ({relatedTarget}: React.MouseEvent) => {
      if (expanded && !subMenuRef.current?.contains(relatedTarget as Element)) {
        setExpanded(false);
      }
    },
    [expanded]
  );

  const label = text;
  const showInactiveTooltip = inactive && Boolean(inactiveText);

  return (
    <ListItem
      key={id}
      ref={setTargetRef}
      aria-disabled={inactive}
      className={classy({
        'another-section': newSection,
        disabled: Boolean(inactive),
      })}
      data-testid={`ListItem-${id}`}
      onClick={handleClick}
      onMouseOut={closeSubMenu}
      onMouseOver={openSubMenu}
    >
      <StartItemWrappers>
        {StartAdornment ? StartAdornment : undefined}
        <MenuItemIcon isLoading={isLoading}>
          {Icon ? <Icon /> : null}
        </MenuItemIcon>
      </StartItemWrappers>
      {showInactiveTooltip ? (
        <ETooltip placement='right' title={inactiveText}>
          <ContextMenuItemLabel as='span'>{label}</ContextMenuItemLabel>
        </ETooltip>
      ) : (
        <EDynamicTooltip placement='right' title={text}>
          <ContextMenuItemLabel>{label}</ContextMenuItemLabel>
        </EDynamicTooltip>
      )}
      {subMenuItems ? <ChevronSmallRight /> : undefined}
      {expanded ? contextSubMenuPopup : null}
    </ListItem>
  );
};

const MenuItemIcon = ({
  isLoading,
  children,
}: {
  isLoading: boolean;
  children: React.ReactNode;
}): React.ReactElement => {
  if (isLoading) {
    return (
      <AnimationWrapper>
        <LoadingIcon />
      </AnimationWrapper>
    );
  }
  return <>{children}</>;
};

export const useContextMenu = <T extends object>(
  actions: ContextAction<T>[],
  item: T
): ReturnType => {
  const [contextMenuPosition, setContextMenuPosition] = useState<
    {x: number; y: number} | undefined
  >(undefined);

  const closeContextMenu = useCallback(
    () => setContextMenuPosition(undefined),
    []
  );

  const boundActions = convertActionsToItems(actions, item, closeContextMenu);

  const openContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();

    setContextMenuPosition({
      x: event.clientX,
      y: event.clientY,
    });
  }, []);

  const {popup} = usePopup({
    disableBackdrop: true,
    content: contextMenuPosition ? (
      <EClickAwayListener
        mouseEvent='onMouseDown'
        onClickAway={() => closeContextMenu()}
      >
        <ContextMenu actionItems={boundActions}></ContextMenu>
      </EClickAwayListener>
    ) : (
      <></>
    ),
    isVisible: Boolean(contextMenuPosition),
    placement: Positioning.TopRightOutsetDown,
    onClose: closeContextMenu,
    top: contextMenuPosition?.y,
    left: contextMenuPosition?.x,
  });

  return {
    contextMenu: contextMenuPosition && actions.length > 0 ? popup : null,
    contextMenuCallbacks: {onContextMenu: openContextMenu},
  };
};

const StartItemWrappers = styled.span`
  display: inline-flex;
  align-items: center;
`;

const StyledContextMenu = styled(List)`
  padding: 0;
  user-select: none;
  display: grid;
  grid-auto-rows: min-content;
  ${mixinOverflow};
  z-index: var(--zindex-modal);
  position: relative;
  cursor: pointer;
  border: var(--regular-border-width) solid var(--color-menu-border-focus);
  border-radius: var(--popover-border-radius);
  font-weight: var(--list-font-weight-text);
  font-size: var(--font-size-default);
  background-color: var(--color-core-white);
  max-height: var(--popup-max-height);
  max-width: var(--popup-max-width);

  & li {
    display: grid;
    color: var(--color-core-link);
    height: var(--context-menu-item-height);
    grid-template-columns: auto 1fr auto;
    align-items: center;
    padding: var(--gutter-tiny) var(--gutter-small);
    overflow: hidden;
    text-overflow: ellipsis;
    column-gap: var(--gutter-little);
  }

  & li.disabled {
    cursor: not-allowed;
    // CSS class 'MUI-disabled' sets an opacity on 'ListItem' which also affects the borders.
    // The 'border-top' of the 'ListItem' is used as section divider.
    // Thus to remove the opacity on the borders, the opacity set by 'MUI-disabled' is unset
    // and re-set only on the child elements
    opacity: unset;

    > * {
      opacity: 0.38; // value copied from 'MUI-disabled' class
    }
  }

  & li:not(.disabled):hover {
    background-color: var(--color-state-hover);
    color: var(--color-core-black);
  }

  & li.another-section {
    border-top: var(--regular-border-width) solid var(--color-menu-border-focus);
  }

  & li svg {
    // work around for new ergon icons
    color: currentColor;
    ${mixinSVGIcon};
  }
`;

const ContextMenuItemLabel = styled.p`
  ${mixinTruncate};
  white-space: nowrap;
`;
